import hark from "hark";
import { useEffect, useRef, useState } from "react";

const SMOOTHING_FACTOR = 0.35;
const DB_FLOOR = -100;
const DB_CEILING = -15;

export interface MicrophoneVolumeVM {
  volumeLevel: number;
  permissionDenied: boolean;
}

export const useSelectedMicrophoneVolume = (
  selectedMicrophoneId: string | null
): MicrophoneVolumeVM => {
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const currentVolumeRef = useRef(0);

  useEffect(() => {
    if (!selectedMicrophoneId) {
      setVolumeLevel(0);
      setPermissionDenied(false);
      return;
    }

    let stream: MediaStream | null = null;
    let harkInstance: ReturnType<typeof hark> | null = null;
    let isDisposed = false;

    currentVolumeRef.current = 0;
    setVolumeLevel(0);
    setPermissionDenied(false);

    const stopStream = (targetStream: MediaStream) => {
      for (const track of targetStream.getTracks()) {
        track.stop();
      }
    };

    const resetVolume = () => {
      currentVolumeRef.current = 0;
      setVolumeLevel(0);
    };

    const requestStream = async (): Promise<MediaStream | null> => {
      try {
        return await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: selectedMicrophoneId } },
        });
      } catch {
        if (!isDisposed) {
          setPermissionDenied(true);
        }
        return null;
      }
    };

    const createHarkInstance = (targetStream: MediaStream) => {
      const audioTrack = targetStream.getAudioTracks()[0] ?? null;
      if (!(audioTrack && audioTrack.readyState === "live")) {
        stopStream(targetStream);
        resetVolume();
        return null;
      }

      try {
        return hark(targetStream, {
          interval: 100,
          play: false,
        });
      } catch (error) {
        console.error("Selected microphone volume meter failed to initialize", {
          audioTrackReadyState: audioTrack.readyState,
          error,
          selectedMicrophoneId,
        });
        stopStream(targetStream);
        resetVolume();
        return null;
      }
    };

    const start = async () => {
      stream = await requestStream();
      if (!stream) {
        return;
      }
      if (isDisposed) {
        stopStream(stream);
        return;
      }

      harkInstance = createHarkInstance(stream);
      if (!harkInstance) {
        stream = null;
        return;
      }

      harkInstance.on("volume_change", (dBs: number) => {
        if (isDisposed) {
          return;
        }

        const clamped = Math.max(DB_FLOOR, Math.min(DB_CEILING, dBs));
        const rawVolume = (clamped - DB_FLOOR) / (DB_CEILING - DB_FLOOR);
        const smoothed =
          currentVolumeRef.current * SMOOTHING_FACTOR +
          rawVolume * (1 - SMOOTHING_FACTOR);
        currentVolumeRef.current = smoothed;
        setVolumeLevel(smoothed);
      });
    };

    start().catch(() => {
      if (!isDisposed) {
        setPermissionDenied(true);
      }
    });

    return () => {
      isDisposed = true;
      harkInstance?.stop();
      harkInstance = null;
      if (stream) {
        stopStream(stream);
        stream = null;
      }
      resetVolume();
    };
  }, [selectedMicrophoneId]);

  return { permissionDenied, volumeLevel };
};
